import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import styled from 'styled-components'
import { Hub } from 'aws-amplify'
import { Toaster } from 'react-hot-toast'
import { useSnapshot } from 'valtio'
import { useTranslation } from 'react-i18next'
import posthog from 'posthog-js'

import '/node_modules/flag-icons/css/flag-icons.min.css'

import {
  useTrackSignupStartMutation,
  useGetVerifiedEmailFromTokenLazyQuery,
} from '@app/graphql'
import {
  CurrentUserState,
  exitGuestMode,
  UserAuthState,
  openAppMenuToHelpCenter,
} from '@app/storage'
import { SsoProviderConfigList } from '@app/types'
import { AuthHelper } from '@app/helpers'
import { usePosthogMagicLinkTracking } from './hooks/usePosthogMagicLinkTracking'
import i18n from './libraries/i18n'
import AppRoutes from './components/Router'
import { useFirebasePushSetup } from '@app/hooks/useFirebasePushSetup'
import { checkWebpSupport, handleAuthEvents, setAppHeight } from '@app/helpers/misc'
import { useFollowLinks } from '@app/hooks/useFollowLinks'
import { useCurrentUser } from '@app/hooks/useCurrentUser'
import { useTenantConfig } from '@app/hooks/useTenantConfig'
import SsoConnectingScreen, { SsoScreenStatus } from '@app/components/SsoConnectingScreen'
import ThemeTestToggle from './components/ThemeTestToggle'

function initLanguage() {
  const userLanguage = localStorage.getItem('app_language') || 'en'
  UserAuthState.userLanguageId = userLanguage
  i18n.changeLanguage(userLanguage)
}

initLanguage()

// Minimal view rendered in the Google auth popup tab so the full app
// (and all its GraphQL queries) never initialises there.
const PopupAuthComplete: React.FC = () => {
  const { t } = useTranslation()
  const [done, setDone] = useState(false)

  useEffect(() => {
    let finished = false
    const finish = () => {
      if (finished) return
      finished = true
      sessionStorage.removeItem('vb_google_popup_auth')
      // federatedSignIn navigates away before Amplify can set these in the popup —
      // set them here so the iframe picks up an authenticated session after reload.
      localStorage.setItem('isAuth', 'true')
      localStorage.setItem('auth_type', 'google')
      CurrentUserState.auth_type = 'google'
      exitGuestMode()
      setDone(true)

      // Collect Amplify/Cognito session tokens so the iframe can copy them into
      // its own partitioned storage. Without this the iframe reloads but Amplify
      // finds no tokens and treats the user as logged-out (third-party storage
      // partitioning means the popup's first-party writes are invisible to the iframe).
      const cognitoEntries: Record<string, string> = {}
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && (
          key.startsWith('CognitoIdentityServiceProvider') ||
          key === 'amplify-signin-with-hostedUI'
        )) {
          cognitoEntries[key] = localStorage.getItem(key) ?? ''
        }
      }

      // Primary channel: direct message to the opener window (the iframe).
      // Most reliable — no dependency on BC lifecycle or localStorage event delivery.
      try {
        window.opener?.postMessage({ type: 'GOOGLE_AUTH_COMPLETE', cognitoEntries }, window.location.origin)
      } catch (e) {
        console.warn('[vb:popup] opener postMessage failed', e)
      }

      const bc = new BroadcastChannel('vb-google-auth')
      bc.postMessage({ type: 'GOOGLE_AUTH_COMPLETE', cognitoEntries })
      setTimeout(() => bc.close(), 1000)

      // localStorage storage event as fallback for browsers without BroadcastChannel.
      localStorage.setItem('vb_google_auth_complete', String(Date.now()))

      // Close the popup. The iframe also closes it via event.source.close() when
      // it receives the postMessage above, which is more reliable after cross-origin
      // redirects. This is a belt-and-suspenders fallback.
      setTimeout(() => window.close(), 400)
    }

    const unsubscribe = Hub.listen('auth', ({ payload: { event } }) => {
      if (event === 'signIn' || event === 'cognitoHostedUI') finish()
    })

    // On the initial load (?googleAuthPopup=1) we are about to navigate away via
    // Auth.federatedSignIn(). Skip getCurrentUser() here — the cached session from a
    // previous login would trigger finish() prematurely, which clears the
    // vb_google_popup_auth sessionStorage flag before the redirect happens.
    // On return from Cognito (?code=...) the flag is absent, so we DO check here.
    const isInitialPopupLoad = new URLSearchParams(window.location.search).has('googleAuthPopup')
    if (!isInitialPopupLoad) {
      AuthHelper.getCurrentUser()
        .then(({ data }) => { if (data) finish() })
        .catch(console.error)
    }

    return () => unsubscribe()
  }, [])

  return (
    <PopupOverlay>
      <PopupMessage>{done ? t('application.loginSuccessfulYouCanCloseThis') : t('application.completingLogin')}</PopupMessage>
    </PopupOverlay>
  )
}

const PopupOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: #0f0f0f;
`

const PopupMessage = styled.p`
  font-size: 16px;
  color: #ffffff;
  font-family: sans-serif;
`

interface ApplicationProps {}

const Application: React.FC<ApplicationProps> = () => {
  // When this window was opened as a Google auth popup (?googleAuthPopup=1),
  // set the sessionStorage flag BEFORE federatedSignIn navigates away, so we
  // can detect the return and render only the minimal completion view.
  const isGoogleAuthPopup = sessionStorage.getItem('vb_google_popup_auth') === '1'
  const params = useMemo(() => new URLSearchParams(window.location.search), [])
  if (params.has('googleAuthPopup')) {
    sessionStorage.setItem('vb_google_popup_auth', '1')
    AuthHelper.authByGoogle(params.get('vbaState') ?? '').catch(console.error)
  }
  // Save the invite token to localStorage once on mount.
  // Must use localStorage (not sessionStorage) because clicking the magic link
  // email opens a new tab where sessionStorage would be empty.
  // IMPORTANT: this must be a useEffect (not render-body code) so it only runs
  // once. The render body runs on every re-render; since `params` is memoized
  // from the initial URL, a render-body setItem would re-save the token after
  // the user redeems the invite and clearInviteStorage() removes it, causing
  // both the "accept again" bug and the "refresh redirects back" bug (TR-XXX).
  const vipInviteFromUrl = params.get('vipInvite')
  useEffect(() => {
    if (vipInviteFromUrl) {
      localStorage.setItem('vipInviteToken', vipInviteFromUrl)
    }
  }, []) // intentionally [] — vipInviteFromUrl is derived from memoized params (stable on mount)
  const [getVerifiedEmail] = useGetVerifiedEmailFromTokenLazyQuery({
    fetchPolicy: 'cache-and-network'
  })
  useEffect(() => {
    if (params.has('trlt')) {
      const token = params.get('trlt')
      localStorage.setItem('trlt', token)
      getVerifiedEmail({
        variables: { token },
        onCompleted: (data) => {
          localStorage.setItem('emailForSignIn', data?.verifyEmailToken)
          localStorage.setItem('auth_type', 'magic_link')
          localStorage.setItem('magic_link_email', data?.verifyEmailToken)
        },
        fetchPolicy: 'cache-and-network'
      })
    }
  }, [getVerifiedEmail, params])
  if (isGoogleAuthPopup || params.has('googleAuthPopup')) {
    return <PopupAuthComplete />
  }

  const [ trackSignupStart ] = useTrackSignupStartMutation()
  useCurrentUser(true)
  const { tenantConfig, tenantConfigLoading } = useTenantConfig()
  const { id } = useSnapshot(CurrentUserState)
  const { setupFirebasePush } = useFirebasePushSetup()
  const { handlePosthogMagicLink } = usePosthogMagicLinkTracking()
  const { followChannelByLink, followTipsterByLink, redeemVipInvite } = useFollowLinks()

  // Fetch SSO provider config (only relevant when running inside an iframe).
  // skip when not in iframe to avoid unnecessary network traffic.
  const isInIframe = window.top !== window

  const handleTrackSignupStart = useCallback(() => {
    trackSignupStart().catch(console.error)
  }, [ trackSignupStart ])

  useEffect(() => {

    const handleResize = () => {
      setAppHeight()
    }

    window.addEventListener('resize', handleResize)
    setAppHeight()


    const { affiliateLinkName, inviteLinkName, inviteeTipsterLinkName } = AuthHelper.getSearchParams()
    Object.assign(UserAuthState, { affiliateLinkName, inviteLinkName, inviteeTipsterLinkName })

    const startupTasks = [
      setupFirebasePush(),
      handlePosthogMagicLink(),
      followChannelByLink(),
      followTipsterByLink(),
      Promise.resolve(checkWebpSupport()),
    ]

    Promise.allSettled(startupTasks.map(p => p.catch(console.error)))

    const unsubscribe = Hub.listen('auth', (data) => {
      const event = data.payload?.event
      if (event === 'signIn' || event === 'cognitoHostedUI') {
        localStorage.setItem('isAuth', 'true')
        const username: string = data.payload?.data?.username ?? ''
        if (username.startsWith('google_')) {
          localStorage.setItem('auth_type', 'google')
        } else if (username.toLowerCase().startsWith('facebook_')) {
          localStorage.setItem('auth_type', 'facebook')
        }
        exitGuestMode()
        AuthHelper.restoreAuthQueryParams()
      }
      handleAuthEvents(handleTrackSignupStart, data)
    })

    return () => {
      window.removeEventListener('resize', handleResize)
      unsubscribe()
    }
  }, [ setupFirebasePush, handlePosthogMagicLink, followChannelByLink, followTipsterByLink, handleTrackSignupStart ])

  // When the user becomes authenticated (id transitions from null → value), check
  // for a pending VIP invite and redirect to the subscriptions page.
  useEffect(() => {
    if (id) redeemVipInvite()
  }, [id, redeemVipInvite])

  // When running inside an iframe, listen for the Google auth popup to complete
  // and reload the iframe so the authenticated session takes effect.
  // We reload (not navigate) so the URL stays exactly what the casino embedded —
  // navigating to /feed would change the iframe src, which some casino pages detect
  // and redirect the top frame to our domain.
  useEffect(() => {
    if (window.top === window) return

    console.log('[vb:ml] iframe loaded at:', window.location.href)
    console.log('[vb:ml] document.referrer:', document.referrer)
    console.log('[vb:ml] localStorage state:', {
      auth_type: localStorage.getItem('auth_type'),
      magic_link_email: localStorage.getItem('magic_link_email'),
      isAuth: localStorage.getItem('isAuth'),
      vb_iframe_parent_url: localStorage.getItem('vb_iframe_parent_url'),
    })

    // Persist the parent page URL so buildAuthRedirectUrl can use it even if
    // document.referrer is blocked by a Referrer-Policy header on the casino.
    const referrer = document.referrer
    const ancestorOrigin = window.location.ancestorOrigins?.[0] ?? ''
    if (referrer) {
      localStorage.setItem('vb_iframe_parent_url', referrer)
    } else if (ancestorOrigin) {
      localStorage.setItem('vb_iframe_parent_url', ancestorOrigin)
    }

    // Ask the parent window for its full URL (including path) via postMessage.
    // Referrer-Policy on the casino strips the path from document.referrer so
    // we only get the origin. The casino JS (or Tampermonkey) responds with the
    // full href which we use as the magic link continueUrl base.
    console.log('[vb:ml] sending VB_REQUEST_PARENT_URL to parent')
    window.parent.postMessage({ type: 'VB_REQUEST_PARENT_URL' }, '*')
    const handleParentUrl = (event: MessageEvent) => {
      if (event.data?.type !== 'VB_PARENT_URL' || !event.data?.url) return
      const parentUrl: string = event.data.url
      console.log('[vb:ml] VB_PARENT_URL received from parent:', parentUrl)
      localStorage.setItem('vb_iframe_parent_url', parentUrl)

      // If the parent URL has magic link params (oobCode + iframeMagicLink=1),
      // the casino's C() may have been overridden by Vue's reactive re-render
      // before the iframe could navigate. Self-redirect to /auth-callback so
      // signInWithEmailLink can complete without relying on casino timing.
      if (localStorage.getItem('isAuth')) return
      if (window.location.pathname === '/auth-callback') return
      try {
        const pp = new URLSearchParams(new URL(parentUrl).search)
        if (
          pp.has('oobCode') &&
          (pp.get('iframeMagicLink') === '1' || pp.has('mode')) &&
          pp.has('pwaOrigin')
        ) {
          const callbackUrl = `${window.location.origin}/auth-callback?iframeMagicLink=${encodeURIComponent(parentUrl)}`
          console.log('[vb:ml] magic link detected — redirecting iframe to auth-callback:', callbackUrl)
          window.location.href = callbackUrl
        }
      } catch {
        // malformed URL — ignore
      }
    }
    window.addEventListener('message', handleParentUrl)

    const reloadAfterAuth = (entries: Record<string, string> = {}, authType = 'google') => {
      // Write isAuth and all session tokens into the iframe's own partitioned
      // storage before reloading. The popup runs as a first-party window so its
      // localStorage writes are invisible to the third-party iframe — we copy them
      // over explicitly via postMessage / BroadcastChannel.
      localStorage.setItem('isAuth', 'true')
      localStorage.setItem('auth_type', authType)
      Object.entries(entries).forEach(([key, value]) => localStorage.setItem(key, value))
      window.location.reload()
    }

    const bc = new BroadcastChannel('vb-google-auth')
    bc.onmessage = (event) => {
      if (event.data?.type === 'GOOGLE_AUTH_COMPLETE') {
        bc.close()
        reloadAfterAuth(event.data.cognitoEntries ?? {}, 'google')
      }
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'vb_google_auth_complete') {
        localStorage.removeItem('vb_google_auth_complete')
        reloadAfterAuth({}, 'google')
      }
    }

    // Most reliable channel: direct postMessage from the popup's window.opener.
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      if (event.data?.type === 'GOOGLE_AUTH_COMPLETE') {
        // Close the popup from the opener side — more reliable than window.close()
        // inside the popup after cross-origin redirects (Google, Cognito, back).
        try {
          (event.source as Window)?.close()
        } catch (e) {
          console.warn('[vb:iframe] could not close popup via event.source', e)
        }
        reloadAfterAuth(event.data.cognitoEntries ?? {}, 'google')
      }
    }

    window.addEventListener('storage', handleStorage)
    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener('message', handleMessage)
      window.removeEventListener('message', handleParentUrl)
      bc.close()
    }
  }, [])

  // ------------------------------------------------------------------
  // SSO postMessage listener — handles { type: 'SSO_LOGIN', provider, payload }
  // messages from the host page (e.g. MondCasino) to silently log the user in.
  // Only active when running inside an iframe.
  // Re-registered whenever ssoData changes (query result arrives).
  // ------------------------------------------------------------------
  const { t } = useTranslation()
  const [ ssoStatus, setSsoStatus ] = useState<'idle' | 'loading' | 'error' | 'success'>('idle')
  const [ ssoAttempt, setSsoAttempt ] = useState(1)
  const lastSsoAttemptRef = useRef<{ provider: string; payload: unknown } | null>(null)

  const runSsoLogin = useCallback(async (provider: string, payload: unknown) => {
    lastSsoAttemptRef.current = { provider, payload }
    setSsoAttempt(1)
    setSsoStatus('loading')
    try {
      await AuthHelper.authBySso(provider, payload, (attempt) => setSsoAttempt(attempt))
      // Auth done — keep showing dots until CurrentUserState.id populates.
      // The useEffect below watches id and dismisses the overlay.
    } catch (err) {
      console.error('[vb:sso] SSO login failed after retries:', err)
      posthog.capture('sso_login_failed', {
        provider,
        tenant: window.location.hostname,
        reason: err instanceof Error ? err.message : String(err),
        attempts: err instanceof AuthHelper.SsoLoginError ? err.attempts : undefined,
      })
      setSsoStatus('error')
    }
  }, [])

  useEffect(() => {
    if (!isInIframe || tenantConfigLoading) return

    const ssoProviders = (tenantConfig?.integrationConfig?.ssoProviders ?? []) as SsoProviderConfigList
    const allowedSsoOrigins = ssoProviders.flatMap(p => p.allowedOrigins)

    if (allowedSsoOrigins.length === 0) return

    const handleSsoMessage = (event: MessageEvent) => {
      if (!allowedSsoOrigins.includes(event.origin)) return
      if (event.data?.type !== 'SSO_LOGIN') return

      const { provider, payload } = event.data
      if (!provider || !payload) return

      void runSsoLogin(provider, payload)
    }

    window.addEventListener('message', handleSsoMessage)

    // Notify the parent that the SSO listener is now active.
    // MondCasino should wait for this signal before sending SSO_LOGIN,
    // because the listener only registers after GetSsoProviders query completes.
    for (const origin of allowedSsoOrigins) {
      try { window.parent.postMessage({ type: 'TIPSTERIZER_SSO_READY' }, origin) } catch (_e) { /* cross-origin postMessage may throw — safe to ignore */ }
    }

    return () => window.removeEventListener('message', handleSsoMessage)
  }, [ tenantConfigLoading, tenantConfig, isInIframe, runSsoLogin ])

  const retrySsoLogin = useCallback(() => {
    const lastAttempt = lastSsoAttemptRef.current
    if (!lastAttempt) return
    void runSsoLogin(lastAttempt.provider, lastAttempt.payload)
  }, [ runSsoLogin ])

  const handleSsoContactSupport = useCallback(() => {
    setSsoStatus('idle')
    openAppMenuToHelpCenter()
  }, [])

  // Dismiss the overlay as soon as the user is loaded.
  useEffect(() => {
    if (ssoStatus !== 'loading' || !id) return
    setSsoStatus('idle')
  }, [ssoStatus, id])

  // Derive SSO overlay screen state
  const showSsoOverlay = isInIframe && (ssoStatus === 'loading' || ssoStatus === 'error')
  let ssoScreenStatus: SsoScreenStatus = 'connecting'
  if (ssoStatus === 'error') ssoScreenStatus = 'error'
  else if (ssoAttempt > 1) ssoScreenStatus = 'retrying'

  const brandLogoUrl = useMemo(() => {
    if(tenantConfigLoading) return undefined
    return tenantConfig?.branding?.logoUrl ?? undefined
  }, [ tenantConfigLoading, tenantConfig ])

  // AppRoutes (and its MemoryRouter) must remain mounted throughout the SSO
  // flow so that:
  //   1. The router stays alive and can react to auth-state changes.
  //   2. When onAuthChange fires after signInWithCustomToken, fetchCurrentUser()
  //      populates CurrentUserState.id and the router navigates to /feed while
  //      the SSO overlay is still covering the screen.
  //   3. When the overlay disappears the user is already at /feed — no extra
  //      navigation needed.
  //
  // Previously AppRoutes was replaced by SsoConnectingScreen (early return),
  // which destroyed the MemoryRouter. On re-mount the fresh router started at
  // window.location.pathname ('/') and relied on a passive ProtectedRoute
  // redirect that raced against the async GraphQL user-fetch — often losing.
  return (
    <>
      <Toaster />
      <AppRoutes />
      {showSsoOverlay && (
        <SsoConnectingScreen
          status={ssoScreenStatus}
          attempt={ssoAttempt}
          onRetry={retrySsoLogin}
          onContactSupport={handleSsoContactSupport}
          logoUrl={brandLogoUrl}
        />
      )}
      <ThemeTestToggle />
    </>
  )
}

export default Application
